import { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useTenant } from '../contexts/TenantContext';

/**
 * usePayoutsList — fetches payout_records + decrypts financial cols via the
 * `generic-decrypt-payouts` Edge Function (R2), then groups by agent × period.
 *
 * Each grouped row represents a "commission run" for a manager in a given
 * year/month, with the sum of decrypted amounts and creator breakdown.
 *
 * R1  : tenant_id + agency_id filter (server-side).
 * R10 : payout_records paginated 1000 rows per page.
 * R2  : decryption goes through an authenticated Edge Function. If the
 *       function is not deployed yet (or returns 403/500), all amounts stay
 *       null and the AES badges stay visible — graceful degradation.
 *
 * Params :
 *  - agentId : if provided, narrows to that manager (manager view)
 *
 * Returns :
 *  - rows : array<{
 *      key, agent_id, agent_name, period_year, period_month, status,
 *      creator_count, totalAmount, totalCommission, totalDiamonds,
 *      decryptionAvailable, creators: array<{
 *        id, creator_id, creator_name, amount, commission, diamonds, status
 *      }>
 *    }>
 *  - loading / error / reload
 */
export function usePayoutsList({ agentId = null } = {}) {
  const { tenantId, agencyId, loading: tenantLoading } = useTenant();
  const [raw, setRaw] = useState([]);
  const [agents, setAgents] = useState([]);
  const [creators, setCreators] = useState([]);
  const [decrypted, setDecrypted] = useState({});
  const [decryptionAvailable, setDecryptionAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      // 1) payout_records paginated R10
      const PAGE = 1000;
      const all = [];
      let from = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        let q = supabase
          .from('black_payout_records')
          .select('id, agent_id, creator_id, period_year, period_month, status, diamonds_enc, amount_enc, commission_enc, created_at')
          .eq('tenant_id', tenantId)
          .order('period_year', { ascending: false })
          .order('period_month', { ascending: false });
        if (agencyId) q = q.eq('agency_id', agencyId);
        if (agentId)  q = q.eq('agent_id', agentId);
        const { data, error: e1 } = await q.range(from, from + PAGE - 1);
        if (e1) throw e1;
        const rows = data || [];
        all.push(...rows);
        if (rows.length < PAGE) break;
        from += PAGE;
      }

      // 2) profiles for agent_name resolution
      const agentIds = Array.from(new Set(all.map((r) => r.agent_id).filter(Boolean)));
      let profilesData = [];
      if (agentIds.length > 0) {
        const { data: p } = await supabase
          .from('profiles')
          .select('id, full_name, email, role')
          .in('id', agentIds);
        profilesData = p || [];
      }

      // 3) creator names (cleartext only) for breakdown rows
      const creatorIds = Array.from(new Set(all.map((r) => r.creator_id).filter(Boolean)));
      let creatorsData = [];
      if (creatorIds.length > 0) {
        const { data: c } = await supabase
          .from('black_creators')
          .select('id, display_name, tiktok_username')
          .in('id', creatorIds);
        creatorsData = c || [];
      }

      // 4) Decrypt via Edge Function (batched, max 200 ids per call). On failure,
      //    set decryptionAvailable=false so the UI keeps the AES badges.
      const allIds = all.map((r) => r.id);
      const decryptedMap = {};
      let decryptOk = true;
      if (allIds.length > 0) {
        const BATCH = 200;
        for (let i = 0; i < allIds.length; i += BATCH) {
          const slice = allIds.slice(i, i + BATCH);
          try {
            const { data: out, error: fnErr } = await supabase.functions.invoke('black-decrypt-payouts', {
              body: { payoutIds: slice },
            });
            if (fnErr) throw fnErr;
            if (out && typeof out === 'object') Object.assign(decryptedMap, out);
          } catch (e) {
            console.warn('[usePayoutsList] decrypt batch failed (keeping AES badges):', e?.message || e);
            decryptOk = false;
            break;
          }
        }
      }

      setRaw(all);
      setAgents(profilesData);
      setCreators(creatorsData);
      setDecrypted(decryptedMap);
      setDecryptionAvailable(decryptOk);
    } catch (e) {
      console.error('[usePayoutsList] error:', e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [tenantId, agencyId, agentId]);

  useEffect(() => {
    if (!tenantLoading) fetchAll();
  }, [fetchAll, tenantLoading]);

  // Group by agent_id + period, merge decrypted values
  const rows = useMemo(() => {
    const profileById = new Map(agents.map((p) => [p.id, p]));
    const creatorById = new Map(creators.map((c) => [c.id, c]));
    const groups = new Map();
    for (const r of raw) {
      const key = `${r.agent_id || 'unassigned'}_${r.period_year}_${r.period_month}`;
      const cur = groups.get(key) || {
        key,
        agent_id: r.agent_id,
        agent_name: r.agent_id ? (profileById.get(r.agent_id)?.full_name || profileById.get(r.agent_id)?.email) : null,
        period_year: r.period_year,
        period_month: r.period_month,
        status: r.status,
        creator_count: 0,
        creators: [],
        totalAmount: 0,
        totalCommission: 0,
        totalDiamonds: 0,
        hasAmount: false,
        hasCommission: false,
        hasDiamonds: false,
      };
      const dec = decrypted[r.id] || null;
      const creator = r.creator_id ? creatorById.get(r.creator_id) : null;
      const amount = dec?.amount ?? null;
      const commission = dec?.commission ?? null;
      const diamonds = dec?.diamonds ?? null;
      cur.creator_count += 1;
      cur.creators.push({
        id: r.id,
        creator_id: r.creator_id,
        creator_name: creator?.display_name || creator?.tiktok_username || null,
        amount,
        commission,
        diamonds,
        status: r.status,
      });
      if (amount != null)     { cur.totalAmount     += amount;     cur.hasAmount = true; }
      if (commission != null) { cur.totalCommission += commission; cur.hasCommission = true; }
      if (diamonds != null)   { cur.totalDiamonds   += diamonds;   cur.hasDiamonds = true; }
      // Status of the group = the lowest priority (draft > validated > paid)
      const priority = { draft: 0, validated: 1, paid: 2 };
      if ((priority[r.status] ?? 0) < (priority[cur.status] ?? 0)) cur.status = r.status;
      groups.set(key, cur);
    }
    // Convert "no decrypted values" to null instead of 0 (so the AES badge stays)
    return Array.from(groups.values()).map((g) => ({
      ...g,
      totalAmount:     g.hasAmount     ? g.totalAmount     : null,
      totalCommission: g.hasCommission ? g.totalCommission : null,
      totalDiamonds:   g.hasDiamonds   ? g.totalDiamonds   : null,
      decryptionAvailable,
    })).sort((a, b) => {
      if (a.period_year !== b.period_year) return b.period_year - a.period_year;
      return b.period_month - a.period_month;
    });
  }, [raw, agents, creators, decrypted, decryptionAvailable]);

  return { rows, decryptionAvailable, loading: loading || tenantLoading, error, reload: fetchAll };
}
